import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verifyAccount } from '../services/api';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

type VerificationState = 'loading' | 'success' | 'error';

export const AccountVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [verificationState, setVerificationState] = useState<VerificationState>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');
  const apiUrl = searchParams.get('api'); // Get API URL from query parameter

  useEffect(() => {
    const handleVerification = async () => {
      if (!token) {
        setVerificationState('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        // Pass the API URL if provided (for dev/prod environment detection)
        await verifyAccount(token, apiUrl || undefined);
        setVerificationState('success');
        setMessage('Account verified successfully! You can now close this window and return to the Good Old Maps app.');
      } catch (error: any) {
        setVerificationState('error');
        setMessage(error.message || 'Verification failed. Please try again or contact support if the problem persists.');
      }
    };

    handleVerification();
  }, [token, apiUrl]);

  const renderContent = () => {
    switch (verificationState) {
      case 'loading':
        return (
          <div className="verification-container">
            <div className="verification-icon loading">
              <div className="spinner"></div>
            </div>
            <h1>Account Verification</h1>
            <p className="loading-message">Verifying your account...</p>
          </div>
        );

      case 'success':
        return (
          <div className="verification-container">
            <div className="verification-icon success">✅</div>
            <h1>Account Verified Successfully!</h1>
            <p className="success-message">{message}</p>
            <p className="welcome-message"><strong>Welcome to Good Old Maps!</strong></p>
            <a href="/" className="btn btn-primary">Return to Home</a>
          </div>
        );

      case 'error':
        return (
          <div className="verification-container">
            <div className="verification-icon error">❌</div>
            <h1>Verification Failed</h1>
            <p className="error-message">{message}</p>
            <p className="help-message">Please try again or contact support if the problem persists.</p>
            <a href="/" className="btn btn-secondary">Return to Home</a>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="verification-page">
      <Header />
      <main className="main-content">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};
